import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import {
  SxProps,
  TableProps,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Theme,
  TableSortLabel,
  Stack,
  TableContainer,
  IconButton,
  Collapse,
  useMediaQuery,
} from "@mui/material";
import {
  createColumnHelper,
  getCoreRowModel,
  ColumnDef,
  ExpandedState,
  Row,
  RowData,
  RowSelectionState,
  useReactTable,
  flexRender,
  OnChangeFn,
  ColumnSort,
  getSortedRowModel,
  getExpandedRowModel,
  CellContext,
  VisibilityState,
} from "@tanstack/react-table";
import {
  Fragment,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  ElementType,
  useCallback,
  useMemo,
} from "react";
import { FaIcon } from "../Icon/FaIcon";
import { SkeletonRows } from "./components/SkeletonRows";

export type SrTableProps<TData extends RowData> = {
  id?: string;
  columns: ColumnDef<TData>[];
  data: TData[];
  emptyState?: ReactNode;
  loading?: boolean;
  expansion?: TableExpandOptions;
  selection?: TableSelectionOptions<TData>;
  size?: TableProps["size"];
  sort?: TableSortOptions;
  columnVisibility?: VisibilityState;
  sx?: SxProps<Theme>;
  onRowClick?(row: TData): void;
  getRowCanExpand?(): boolean;
  renderDetailPanel?(props: { row: Row<TData> }): ReactNode;
  /**
   * Determine the unique identifier for a row.
   *
   * Defaults to `row.id` (if it exists) otherwise falls back to the row index
   */
  getRowId?(row: TData, index: number): string;
  slots?: {
    TableHead?: ElementType;
  };
};

export type TableExpandOptions = {
  expanded: ExpandedState;
  setExpanded: OnChangeFn<ExpandedState>;
};

export type TableSortOptions = {
  sorting: SortingState[];
  setSorting: OnChangeFn<SortingState[]>;
  disableServerSorting?: boolean;
};

export type TableSelectionOptions<TData> = {
  rowSelection: RowSelectionState;
  setRowSelection: OnChangeFn<RowSelectionState>;
  multiple?: boolean;
  /** Filter function to determine if a row can be selected */
  filter?(row: RowSelectionFilterArgs<TData>): boolean;
};

export type RowSelectionFilterArgs<TData> = Row<TData>;

export enum SortOrder {
  Asc = "ASC",
  Desc = "DESC",
}

export type SortingState = {
  field: string;
  order: SortOrder;
};

export type TableCellProps<TData extends RowData, TValue = unknown> = CellContext<TData, TValue>;

export type { ColumnDef, ExpandedState, RowSelectionState };
export { createColumnHelper };

export function SrTable<TData extends RowData>({
  id,
  columns,
  data,
  emptyState,
  loading,
  expansion,
  getRowId = defaultGetRowId<TData>,
  getRowCanExpand,
  renderDetailPanel,
  selection,
  size,
  sort,
  columnVisibility,
  sx,
  onRowClick,
  slots,
}: SrTableProps<TData>): ReactElement {
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.only("xs"));
  const [sorting, onSortingChange] = useSortState(sort);
  const { expanded, setExpanded: onExpandedChange } = expansion ?? {};
  const { rowSelection, setRowSelection: onRowSelectionChange } = selection ?? {};
  const { TableHead: TH = TableHead } = slots ?? {};

  // oxlint-disable-next-line react/incompatible-library -- TanStack Table v8 returns a mutable instance
  const table = useReactTable({
    columns,
    data,
    manualSorting: !sort?.disableServerSorting,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: expansion ? getExpandedRowModel() : undefined,
    getSortedRowModel: sort ? getSortedRowModel() : undefined,
    enableRowSelection: selection?.filter ?? !!selection,
    enableMultiRowSelection: selection?.multiple,
    getSubRows,
    getRowId,
    getRowCanExpand,
    onExpandedChange,
    onRowSelectionChange,
    onSortingChange,
    state: {
      rowSelection,
      expanded,
      sorting,
      columnVisibility,
    },
  });

  const { rows } = table.getRowModel();

  return (
    <TableContainer sx={sx}>
      <Table
        id={id}
        size={size}
        sx={[
          rows.some((row) => row.getCanExpand) && {
            borderCollapse: "separate",
            borderSpacing: "0 5px",
          },
        ]}
      >
        {rows.length ? (
          <TH>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = !!sort && header.column.getCanSort();
                  const isSorted = header.column.getIsSorted();
                  const direction = isSorted !== false ? isSorted : undefined;
                  const content = flexRender(header.column.columnDef.header, header.getContext());

                  return (
                    <TableCell
                      key={header.id}
                      className={header.id}
                      colSpan={header.colSpan}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {canSort ? (
                        <TableSortLabel active={!!isSorted} direction={direction}>
                          {content}
                        </TableSortLabel>
                      ) : (
                        content
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TH>
        ) : null}
        <TableBody>
          {!loading && !rows.length && emptyState ? (
            <TableRow>
              <TableCell colSpan={columns.length}>
                <Stack direction="row" justifyContent="center" sx={{ mt: 8 }}>
                  {emptyState}
                </Stack>
              </TableCell>
            </TableRow>
          ) : null}
          {rows.map((row) => {
            const canExpand = row.getCanExpand();
            const isExpanded = row.getIsExpanded();
            const isSelected = row.getCanSelect() && row.getIsSelected();
            const toggleExpanded = row.getToggleExpandedHandler();
            const onClick =
              onRowClick || (expansion && canExpand)
                ? () => {
                    if (expansion) toggleExpanded();
                    onRowClick?.(row.original);
                  }
                : undefined;

            return (
              <Fragment key={row.id}>
                <TableRow
                  onClick={onClick}
                  selected={isSelected}
                  sx={[
                    canExpand && { backgroundColor: "background.default" },
                    isExpanded && {
                      "& td:first-of-type": {
                        borderBottomLeftRadius: "0!important",
                      },
                    },
                    isExpanded && {
                      "& td:last-of-type": {
                        borderBottomRightRadius: "0!important",
                      },
                    },
                    !!onClick && {
                      "@media (hover: hover)": {
                        "&:hover": { backgroundColor: "background.secondary" },
                      },
                    },
                    !!onClick && !isSelected && { cursor: "pointer" },
                  ]}
                  className={`Sr-TableRowDepth-${row.depth}${canExpand ? " canExpand" : ""}${isExpanded ? " isExpanded" : ""}`}
                >
                  {row.getVisibleCells().map((cell, i) => {
                    const content = flexRender(cell.column.columnDef.cell, cell.getContext());

                    return (
                      <TableCell
                        key={cell.id}
                        className={cell.column.id}
                        sx={expansion && i === 0 && isMobile ? { verticalAlign: "top" } : {}}
                      >
                        {expansion && i === 0 ? (
                          <ExpandableWrapper
                            canExpand={canExpand}
                            depth={row.depth}
                            isExpanded={isExpanded}
                          >
                            {content}
                          </ExpandableWrapper>
                        ) : (
                          content
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
                {renderDetailPanel ? (
                  <TableRow className="detailPanel">
                    <TableCell
                      sx={{
                        p: 0,
                        borderBottom: "none",
                        height: isExpanded ? "initial" : "0!important",
                      }}
                      colSpan={row.getVisibleCells().length}
                    >
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        {renderDetailPanel({ row })}
                      </Collapse>
                    </TableCell>
                  </TableRow>
                ) : null}
              </Fragment>
            );
          })}
          <SkeletonRows loading={!!loading} columns={isMobile ? 3 : columns.length} />
        </TableBody>
      </Table>
    </TableContainer>
  );
}

SrTable.createBuilder = function createBuilder<TData extends RowData>() {
  return {
    ...createColumnHelper<TData>(),
    // Helper method to workaround https://github.com/TanStack/table/issues/4302
    // (`createColumnHelper` output types cannot be inferred correctly)
    columns(...columns: ColumnDef<TData, never>[]) {
      return columns as ColumnDef<TData>[];
    },
  };
};

type ExpandableWrapperProps = {
  canExpand: boolean;
  depth: number;
  isExpanded: boolean;
};

function ExpandableWrapper({
  canExpand,
  depth,
  isExpanded,
  children,
}: PropsWithChildren<ExpandableWrapperProps>) {
  return (
    <Stack
      sx={{
        flexDirection: "row",
        alignItems: "center",
        pl: (theme) => `calc(var(--depth) * ${theme.spacing(3)} + var(--offset))`,
      }}
      style={{ "--depth": depth, "--offset": !canExpand ? "30px" : "0px" } as never}
    >
      {canExpand ? (
        <IconButton
          className="expander"
          aria-label="expand row"
          component="div"
          sx={{ mr: { xs: 0, md: 1 }, fontSize: 15 }}
        >
          <FaIcon
            icon={faChevronRight}
            className={isExpanded ? "expanded" : ""}
            sx={{
              "&.expanded": { transform: "rotate(90deg)" },
              transition: (theme) => theme.transitions.create("transform", { duration: 200 }),
            }}
          />
        </IconButton>
      ) : null}
      {children}
    </Stack>
  );
}

function getSubRows<TData>(row: TData): TData[] | undefined {
  return typeof row === "object" && !!row && "subRows" in row
    ? (row.subRows as TData[])
    : undefined;
}

function mapSortingState(state: SortingState): ColumnSort {
  return {
    id: state.field,
    desc: state.order === SortOrder.Desc,
  };
}

function mapColumnSort(sort: ColumnSort): SortingState {
  return {
    field: sort.id,
    order: sort.desc ? SortOrder.Desc : SortOrder.Asc,
  };
}

function useSortState(
  sort: TableSortOptions | undefined,
): [ColumnSort[], OnChangeFn<ColumnSort[]>] {
  const sorting = useMemo(() => sort?.sorting?.map(mapSortingState) ?? [], [sort?.sorting]);

  const onSortingChange: OnChangeFn<ColumnSort[]> = useCallback(
    (newSorting) => {
      const setValue = sort?.setSorting;
      if (!setValue) return;

      if (typeof newSorting === "function") {
        setValue((prev) => newSorting(prev.map(mapSortingState)).map(mapColumnSort));
        return;
      }

      setValue(newSorting.map(mapColumnSort));
    },
    [sort?.setSorting],
  );

  return [sorting, onSortingChange];
}

function defaultGetRowId<TData extends RowData>(row: TData, index: number): string {
  return typeof row === "object" && !!row && "id" in row && typeof row.id === "string"
    ? row.id
    : index.toString();
}
